import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  LinearProgress,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED"]);

export type BulkRemovePacksScope = "selected" | "filters";

export type BulkRemovePacksJobProgress = {
  status: string;
  totalCount: number;
  processedCount: number;
  removedCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
};

interface BulkRemovePacksPopupProps {
  open: boolean;
  scope: BulkRemovePacksScope;
  count: number;
  submitting: boolean;
  error: string | null;
  job: BulkRemovePacksJobProgress | null;
  onConfirm: () => void;
  onClose: () => void;
}

const CountText: React.FC<{ value: number; color?: string }> = ({
  value,
  color,
}) => (
  <Box component="span" sx={{ color, fontWeight: 700 }}>
    {value}
  </Box>
);

const BulkRemovePacksPopup: React.FC<BulkRemovePacksPopupProps> = ({
  open,
  scope,
  count,
  submitting,
  error,
  job,
  onConfirm,
  onClose,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);

  const isValid = count > 0 && !submitting;
  const shipmentWord = count === 1 ? "shipment" : "shipments";
  const confirmLabel = submitting ? "Starting…" : "Remove packs";

  const jobRunning = Boolean(job && !TERMINAL_STATUSES.has(job.status));
  const busy = jobRunning || submitting;
  const expectedTotal =
    job && job.totalCount > 0
      ? job.totalCount
      : scope === "selected" && count > 0
        ? count
        : (job?.totalCount ?? 0);
  const processed = job
    ? Math.max(
        job.processedCount,
        job.removedCount + job.failedCount + job.skippedCount,
      )
    : 0;
  const progressPct =
    expectedTotal > 0
      ? Math.min(100, Math.round((processed / expectedTotal) * 100))
      : jobRunning
        ? undefined
        : 0;

  const jobStatusLine = !job
    ? null
    : job.status === "COMPLETED"
      ? `Done. Removed ${job.removedCount}, skipped ${job.skippedCount}, failed ${job.failedCount}.`
      : job.status === "FAILED"
        ? `Stopped. Removed ${job.removedCount}, skipped ${job.skippedCount}, failed ${job.failedCount}.`
        : expectedTotal > 0
          ? `Removing… ${processed} of ${expectedTotal} (removed ${job.removedCount}, skipped ${job.skippedCount}, failed ${job.failedCount}).`
          : `Removing… removed ${job.removedCount}, skipped ${job.skippedCount}, failed ${job.failedCount}.`;

  const handleDialogClose = (
    _event: unknown,
    reasonCode?: "backdropClick" | "escapeKeyDown",
  ) => {
    if (
      busy &&
      (reasonCode === "backdropClick" || reasonCode === "escapeKeyDown")
    ) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={busy}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <CustomTypography variant="heading6" fontWeight={600}>
          Remove packs
        </CustomTypography>
      </DialogTitle>
      <DialogContent>
        <Grid2 container spacing={1.5}>
          {job ? (
            <Grid2 size={12}>
              <CustomTypography variant="body2">{jobStatusLine}</CustomTypography>
              {jobRunning && (
                <>
                  <Box sx={{ mt: 1.5 }}>
                    <LinearProgress
                      variant={
                        progressPct === undefined
                          ? "indeterminate"
                          : "determinate"
                      }
                      value={progressPct}
                    />
                  </Box>
                  <CustomTypography
                    variant="body2"
                    sx={{ color: colors.subtitle, mt: 1 }}
                  >
                    Safe to close — reopen from Actions to check progress.
                  </CustomTypography>
                </>
              )}
              {job.errorMessage && (
                <Box sx={{ mt: 1 }}>
                  <InlineFormError message={job.errorMessage} />
                </Box>
              )}
              {error && (
                <Box sx={{ mt: 1 }}>
                  <InlineFormError message={error} />
                </Box>
              )}
            </Grid2>
          ) : (
            <>
              <Grid2 size={12}>
                <CustomTypography variant="body2">
                  Remove packs from{" "}
                  <CountText
                    value={count}
                    color={theme.palette.customColors?.errorRed}
                  />{" "}
                  {scope === "selected" ? "selected" : "matching"}{" "}
                  {shipmentWord}? SKUs stay on the shipment.
                </CustomTypography>
              </Grid2>
              <Grid2 size={12}>
                <InlineFormError message={error ?? undefined} />
              </Grid2>
            </>
          )}
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
        <AppButton
          variant="secondary"
          label="Close"
          onClick={onClose}
          disabled={submitting}
        />
        {!job && (
          <AppButton
            variant="primary"
            label={confirmLabel}
            onClick={onConfirm}
            disabled={!isValid}
            customStyles={{
              backgroundColor:
                theme.palette.customColors?.errorRed ||
                theme.palette.error.main,
              color: theme.palette.common.white,
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.errorRed ||
                  theme.palette.error.main,
                filter: "brightness(0.92)",
              },
              "&.Mui-disabled": {
                backgroundColor:
                  theme.palette.customColors?.errorRed ||
                  theme.palette.error.main,
                color: theme.palette.common.white,
                opacity: 0.5,
              },
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkRemovePacksPopup;
